const GYG_WIDGET_SCRIPT_URL = 'https://widget.getyourguide.com/v2/widget.js';
const GYG_WIDGET_SCRIPT_ID = 'gyg-widget-script';

let loadPromise: Promise<void> | null = null;
let scriptLoaded = false;

type GygWidgetWindow = Window & {
  GYG?: {
    Widget?: {
      process?: () => void;
    };
  };
};

/**
 * Whether the GYG widget script has finished loading in this session.
 */
export function isGygWidgetScriptLoaded(): boolean {
  return scriptLoaded;
}

/**
 * Loads the GetYourGuide widget script once per session (singleton promise).
 */
export function loadGygWidgetScript(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('GetYourGuide widget script requires a browser document'));
  }

  if (scriptLoaded) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(GYG_WIDGET_SCRIPT_ID);
    if (existing) {
      scriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = GYG_WIDGET_SCRIPT_ID;
    script.src = GYG_WIDGET_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load GetYourGuide widget script'));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}

/**
 * Notifies the GYG script to process newly mounted widget containers.
 */
export function notifyGygWidgetsOfNewContainer(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const win = window as GygWidgetWindow;
  win.GYG?.Widget?.process?.();
}

/** Test-only reset for singleton state. */
export function resetGygWidgetScriptStateForTests(): void {
  loadPromise = null;
  scriptLoaded = false;
}
